import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import api from '../../../utils/api';
import { exportDataToCSV } from '../../../utils/exportUtils';

const COLORS = ['#1E3A5F', '#4A90A4', '#F39C12', '#27AE60', '#E74C3C', '#9B59B6', '#34495E'];

const getCategories = (category) => {
  if (Array.isArray(category)) {
    const categories = category.filter(Boolean);
    return categories.length ? categories : ['Uncategorized'];
  }
  return category ? [category] : ['Uncategorized'];
};

const InventoryAnalytics = ({ dateFrom, dateTo, categoryFilter, refreshKey }) => {
  const [activeChart, setActiveChart] = useState('trends');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    let active = true;
    const fetchAnalytics = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/reports/analytics');
        if (active) {
          setProducts(Array.isArray(data.products) ? data.products : []);
          setSales(Array.isArray(data.sales) ? data.sales : []);
        }
      } catch (requestError) {
        console.error('Failed to fetch report analytics:', requestError);
        if (active) setError('Analytics could not be loaded. Please refresh to try again.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchAnalytics();
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const filteredProducts = useMemo(() => (
    categoryFilter === 'all'
      ? products
      : products.filter(product => getCategories(product.category).includes(categoryFilter))
  ), [products, categoryFilter]);

  const filteredSales = useMemo(() => sales.filter(sale => {
    const date = new Date(sale.createdAt);
    if (Number.isNaN(date.getTime()) || date < dateFrom || date > dateTo) return false;
    return categoryFilter === 'all' || getCategories(sale.category).includes(categoryFilter);
  }), [sales, dateFrom, dateTo, categoryFilter]);

  const monthlySales = useMemo(() => {
    const firstMonth = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), 1);
    const lastMonth = new Date(dateTo.getFullYear(), dateTo.getMonth(), 1);
    const monthCount = (lastMonth.getFullYear() - firstMonth.getFullYear()) * 12
      + lastMonth.getMonth() - firstMonth.getMonth();
    const months = [];
    for (let offset = 0; offset <= monthCount; offset += 1) {
      const date = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + offset, 1);
      months.push({
        key: `${date.getFullYear()}-${date.getMonth()}`,
        month: date.toLocaleString(undefined, { month: 'short', year: '2-digit' }),
        unitsSold: 0
      });
    }
    const byMonth = new Map(months.map(month => [month.key, month]));
    filteredSales.forEach(sale => {
      const date = new Date(sale.createdAt);
      const month = byMonth.get(`${date.getFullYear()}-${date.getMonth()}`);
      if (month) month.unitsSold += sale.quantity;
    });
    return months;
  }, [filteredSales, dateFrom, dateTo]);

  const categoryData = useMemo(() => {
    const totals = new Map();
    filteredProducts.forEach(product => {
      const value = (Number(product.cost) || 0) * (Number(product.quantity) || 0);
      const categories = getCategories(product.category);
      const share = value / categories.length;
      categories.forEach(category => {
        const total = totals.get(category) || { value: 0, count: 0 };
        total.value += share;
        total.count += 1;
        totals.set(category, total);
      });
    });
    const totalValue = Array.from(totals.values()).reduce((sum, item) => sum + item.value, 0);
    return Array.from(totals.entries())
      .map(([name, total], index) => ({
        name,
        value: totalValue > 0 ? Number(((total.value / totalValue) * 100).toFixed(1)) : 0,
        count: total.count,
        color: COLORS[index % COLORS.length]
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredProducts]);

  const dailySales = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const totals = days.map(day => ({ day, unitsSold: 0 }));
    filteredSales.forEach(sale => {
      totals[new Date(sale.createdAt).getDay()].unitsSold += sale.quantity;
    });
    return totals;
  }, [filteredSales]);

  const chartTypes = [
    { id: 'trends', label: 'Monthly Sales', icon: 'LineChart' },
    { id: 'categories', label: 'Inventory by Category', icon: 'PieChart' },
    { id: 'movements', label: 'Sales by Weekday', icon: 'BarChart2' }
  ];

  const handleExportData = () => {
    if (activeChart === 'trends') {
      exportDataToCSV(['Month', 'Units Sold'], monthlySales.map(item => [item.month, item.unitsSold]), 'monthly-sales');
    } else if (activeChart === 'categories') {
      exportDataToCSV(
        ['Category', 'Inventory Cost Value Share', 'Product Count'],
        categoryData.map(item => [item.name, `${item.value}%`, item.count]),
        'inventory-by-category'
      );
    } else {
      exportDataToCSV(['Weekday', 'Units Sold'], dailySales.map(item => [item.day, item.unitsSold]), 'sales-by-weekday');
    }
  };

  const hasChartData = activeChart === 'trends'
    ? monthlySales.some(item => item.unitsSold > 0)
    : activeChart === 'categories'
      ? categoryData.length > 0
      : filteredSales.length > 0;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Inventory Analytics</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {chartTypes.map(type => (
            <Button
              key={type.id}
              variant={activeChart === type.id ? 'default' : 'outline'}
              size="sm"
              iconName={type.icon}
              iconPosition="left"
              iconSize={16}
              onClick={() => setActiveChart(type.id)}
            >
              {type.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="h-80">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading analytics...</div>
        ) : error ? (
          <div role="alert" className="h-full flex items-center justify-center text-sm text-destructive">{error}</div>
        ) : !hasChartData ? (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            {activeChart === 'categories'
              ? 'No inventory data is available.'
              : 'No completed sales exist for this period and category.'}
          </div>
        ) : activeChart === 'trends' ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlySales} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
              <XAxis dataKey="month" stroke="#6B7280" />
              <YAxis stroke="#6B7280" allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="unitsSold" name="Units sold" stroke="#4A90A4" strokeWidth={2} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : activeChart === 'categories' ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                dataKey="value"
                nameKey="name"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {categoryData.map(item => <Cell key={item.name} fill={item.color} />)}
              </Pie>
              <Tooltip formatter={(value, _name, item) => [`${value}% (${item.payload.count} products)`, 'Inventory cost value']} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailySales} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
              <XAxis dataKey="day" stroke="#6B7280" />
              <YAxis stroke="#6B7280" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="unitsSold" name="Units sold" fill="#4A90A4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-border flex justify-between items-center gap-3">
        <div>
          <h3 className="text-sm font-medium text-foreground">Analysis Summary</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {activeChart === 'trends' && 'Units sold per month, based on completed checkout history.'}
            {activeChart === 'categories' && 'Current inventory cost value share by product category.'}
            {activeChart === 'movements' && 'Completed units sold by weekday in the selected date range.'}
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={16} onClick={handleExportData} disabled={loading || !!error}>
          Export Data
        </Button>
      </div>
    </div>
  );
};

export default InventoryAnalytics;
